import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment-timezone';
import { Audits } from '@core/models/audits.model';
import { AuditsDataService } from './services/audits-data.service';
import { AuthHttpService } from '@core/services/auth-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'kt-audits',
  templateUrl: './audits.component.html',
  styleUrls: ['./audits.component.scss']
})
export class AuditsComponent implements OnInit {
  form: FormGroup;
  ranges: any;
  maxDate: any;
  dateTimePickerLocale: any;

  audits: Audits[] = [];
  page = 1;
  pageSize = 30;
  params = '';
  loading = false;
  loadmore = true;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;

  constructor(
    private cdr: ChangeDetectorRef,
    private authHttpService: AuthHttpService,
    private transactionHttpService: TransactionHttpService,
    private auditsDataService: AuditsDataService,
  ) { }

  ngOnInit(): void {
    this.ranges = this.transactionHttpService.ranges;
    this.dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;

    this.formInit();
    this.onSubmit();
  }

  @HostListener("window:scroll", [])
  onScroll(): void {
    const documentHeight = Math.max(
      document.body.scrollHeight || 0,
      document.body.offsetHeight || 0,
      document.documentElement.scrollHeight || 0,
      document.documentElement.offsetHeight || 0
    );
    const scrollbottom = (window.innerHeight + window.scrollY) * 1.1 >= documentHeight;

    if (this.loadmore && !this.loading && scrollbottom) {
      this.onSubmit(true);
    }
  }

  onClear() {
    this.formInit();
    this.onSubmit();
  }

  toLowerCaseInput(controlName: string, event: Event) {
    this.authHttpService.forceLowerCaseInputControl(this.form, controlName, event);
  }

  onDateOpened() {
    this.maxDate = moment();

    const service = this.transactionHttpService;

    const todayservice = service.getToday();
    this.ranges.Today = this.patchDatetime(todayservice.from, todayservice.to);

    const last24hourservice = service.getLast24Hours();
    this.ranges['Last 24 Hours'] = this.patchDatetime(last24hourservice.from, last24hourservice.to);

    const yesterdayservice = service.getYesterday();
    this.ranges.Yesterday = this.patchDatetime(yesterdayservice.from, yesterdayservice.to);

    const thisweekservice = service.getThisWeek();
    this.ranges['This Week'] = this.patchDatetime(thisweekservice.from, thisweekservice.to);

    const thismonthservice = service.getThisMonth();
    this.ranges['This Month'] = this.patchDatetime(thismonthservice.from, thismonthservice.to);
  }

  onDateChange(event: any = null) {
    const patchdatetime = this.patchDatetime(event?.startDate, event?.endDate);

    this.maxDate = moment();

    this.form.patchValue({
      start_date: patchdatetime.startdate,
      end_date: patchdatetime.enddate,
      defaultDate: {
        startDate: patchdatetime.startdate,
        endDate: patchdatetime.enddate,
      }
    });
  }

  onSubmit(scrollload?: boolean) {
    this.loading = true;
    this.page++;

    if (!scrollload) {
      this.page = 1;
      this.params = this.getSearchParams();
      this.loadmore = true;
      this.audits = [];
    }

    let queryparam = `page=${this.page}&perPage=${this.pageSize}&${this.params}`;
    this.auditsDataService.getWithQuery(queryparam)
    .pipe(
      catchError((error) => {
        this.loading = false;
        this.loadmore = false;

        // Throw the error as usual
        throw error;
      })
    )
    .subscribe((res: Audits[]) => {
      this.loading = false;
      if (res) {
        this.audits = [...this.audits, ...res];
        this.cdr.detectChanges();

        if (res.length >= this.pageSize) {
          return;
        }
      }

      this.loadmore = false;
    });
  }

  private formInit() {
    const patchdatetime = this.patchDatetime();

    this.maxDate = moment();

    this.form = new FormGroup({
      username: new FormControl(null),
      unique_id: new FormControl(null),
      ip_address: new FormControl(null),
      event: new FormControl('all'),
      url: new FormControl(null),
      modal_id: new FormControl(null),
      modal_name: new FormControl(null),
      keyword: new FormControl(null),
      start_date: new FormControl(patchdatetime.startdate),
      end_date: new FormControl(patchdatetime.enddate),
      defaultDate: new FormControl({
        startDate: patchdatetime.startdate,
        endDate: patchdatetime.enddate,
      }), // Do not remove: For Clearing The Range
    });
  }

  private getSearchParams(): string {
    const formValues = this.form.value;
    const patchdatetime = this.patchDatetime(formValues.start_date, formValues.end_date);

    const newFormValues = {
      ...formValues,
      start_date: patchdatetime.startdate.tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss'),
      end_date: patchdatetime.enddate.tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss'),
    };

    const filteredFormValues = this.filterFormFields(newFormValues);
    const searchParams = new URLSearchParams(filteredFormValues);

    return searchParams.toString();
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] && formData[key] !== 'all' && key !== 'defaultDate') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  private patchDatetime(start: any = null, end:any = null) {
    if (start) start = moment(start).clone();
    if (end) end = moment(end).clone();

    let enddate = end && !end.isAfter(moment()) ? end : moment().subtract(1, 'minute');
    let startdate = start && !start.isAfter(moment()) ? start : enddate.clone().subtract(1, 'hour');

    startdate = startdate.clone();
    enddate = enddate.clone();

    return {startdate, enddate};
  }
}
